/**
 * FormEventHandler.js
 * A global service that handles form submissions through events
 * Can use existing hooks if available, or fall back to direct implementations
 */

class FormEventHandler {
  constructor() {
    this.handlers = {
      customer_register: this.handleRegister,
      register: this.handleRegister,
      recover_customer_password: this.handlePasswordRecovery,
      contact: this.handleContact
    };

    // Registry for active hooks
    this.hookRegistry = {};

    // Debug mode - set to true for verbose console logging
    this.debugMode = true;
  }

  /**
   * Log debug messages if debug mode is enabled
   */
  debug(...args) {
    if (this.debugMode) {
      console.log('[FormEventHandler]', ...args);
    }
  }

  /**
   * Initialize the form event handler
   */
  init() {
    // Add the event listener for form submissions
    window.addEventListener('formSubmitted', this.handleFormSubmit.bind(this));
    this.debug('Initialized and listening for form events');

    // Add listeners for hook registration/unregistration
    window.addEventListener('hookRegistered', this.registerHook.bind(this));
    window.addEventListener('hookUnregistered', this.unregisterHook.bind(this));

    // Print diagnostic info
    this.debug('Available form handlers:', Object.keys(this.handlers));
  }

  /**
   * Register a hook instance
   * @param {Event} event - The hook registration event
   */
  registerHook(event) {
    const { hookType, hookInstance } = event.detail;

    if (hookType && hookInstance) {
      this.hookRegistry[hookType] = hookInstance;
      this.debug(`Registered hook for ${hookType}`, hookInstance);
    } else {
      this.debug('Invalid hook registration attempt:', event.detail);
    }

    // Print current registry status
    this.debug('Current hook registry:', Object.keys(this.hookRegistry));
  }

  /**
   * Unregister a hook instance
   * @param {Event} event - The hook unregistration event
   */
  unregisterHook(event) {
    const { hookType } = event.detail;

    if (hookType && this.hookRegistry[hookType]) {
      this.debug(`Unregistering hook for ${hookType}`);
      delete this.hookRegistry[hookType];
    } else {
      this.debug(`Attempted to unregister non-existent hook: ${hookType}`);
    }

    // Print current registry status
    this.debug('Current hook registry:', Object.keys(this.hookRegistry));
  }

  /**
   * Handle form submission events
   * @param {Event} event - The form submission event
   */
  handleFormSubmit(event) {
    const { formId, formType, formFields } = event.detail;

    this.debug(`Received form submission: ${formId} of type "${formType}"`, formFields);

    // Check if a hook is registered for this form type
    if (formType === 'customer_login' || formType === 'login') {
      const loginHook = this.hookRegistry['customer_login'];
      if (loginHook) {
        this.debug('Using registered customer_login hook for form handling');
        this.handleLoginWithHook(formFields, loginHook);
        return;
      } else {
        this.debug('No customer_login hook found, falling back to default implementation');
      }
    } else if (formType === 'customer_register' || formType === 'register') {
      const registerHook = this.hookRegistry['customer_register'];
      if (registerHook) {
        this.debug('Using registered customer_register hook for form handling');
        this.handleRegisterWithHook(formFields, registerHook);
        return;
      } else {
        this.debug('No customer_register hook found, falling back to default implementation');
      }
    }

    // Otherwise fall back to the default handler
    const handler = this.handlers[formType];

    if (handler) {
      this.debug(`Using default handler for form type "${formType}"`);
      handler.call(this, formFields);
    } else {
      console.warn(`FormEventHandler: No handler registered for form type "${formType}"`);
    }
  }

  /**
   * Handle login using the registered hook
   * @param {Object} formFields - The form fields
   * @param {Object} loginHook - The registered login hook
   */
  async handleLoginWithHook(formFields, loginHook) {
    this.debug('Processing login with hook');

    // Extract email and password from form fields
    const email = formFields['customer[email]'] || formFields.email;
    const password = formFields['customer[password]'] || formFields.password;
    const newsletterOptin = formFields['customer[accepts_marketing]'] === '1' ||
                            formFields.accepts_marketing === '1' ||
                            formFields.newsletter === 'on';

    if (!email || !password) {
      console.error('FormEventHandler: Missing email or password for login');
      return;
    }

    // Call the hook's login method
    if (typeof loginHook.loginCustomer === 'function') {
      this.debug(`Calling loginCustomer hook function for ${email}`);

      await loginHook.loginCustomer(email, password, newsletterOptin);
    } else {
      console.error('FormEventHandler: Registered login hook does not have loginCustomer method');
      // Fall back to default implementation
      this.handleLogin(formFields);
    }
  }

  /**
   * Handle registration using the registered hook
   * @param {Object} formFields - The form fields
   * @param {Object} registerHook - The registered register hook
   */
  handleRegisterWithHook(formFields, registerHook) {
    this.debug('Processing registration with hook');

    // Call the hook's register method if it exists
    if (typeof registerHook.createCustomer === 'function') {
      this.debug('Calling createCustomer hook function');
      registerHook.createCustomer(formFields);
    } else {
      console.error('FormEventHandler: Registered register hook does not have createCustomer method');
      // Fall back to default implementation
      this.handleRegister(formFields);
    }
  }

  /**
   * Handle customer login forms (fallback implementation)
   * @param {Object} formFields - The form fields
   */
  handleLogin(formFields) {
    this.debug('Processing login form (fallback implementation)');

    // Extract email and password from form fields
    const email = formFields['customer[email]'] || formFields.email;
    const password = formFields['customer[password]'] || formFields.password;
    const newsletterOptin = formFields['customer[accepts_marketing]'] === '1' ||
                            formFields.accepts_marketing === '1' ||
                            formFields.newsletter === 'on';

    if (!email || !password) {
      console.error('FormEventHandler: Missing email or password for login');
      return;
    }

    // Create the form submission
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/login';
    form.style.display = 'none'; // Hide the form

    // Create form inputs
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = email;
    form.appendChild(emailInput);

    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = password;
    form.appendChild(passwordInput);

    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'customer_login';
    form.appendChild(formTypeInput);

    const utf8Input = document.createElement('input');
    utf8Input.type = 'hidden';
    utf8Input.name = 'utf8';
    utf8Input.value = '✓';
    form.appendChild(utf8Input);

    // Add newsletter signup if enabled
    if (newsletterOptin) {
      const newsletterInput = document.createElement('input');
      newsletterInput.type = 'hidden';
      newsletterInput.name = 'customer[accepts_marketing]';
      newsletterInput.value = '1';
      form.appendChild(newsletterInput);
    }

    // Append the form to the document body
    document.body.appendChild(form);

    // Use Shopify's CAPTCHA protection if available
    if (window.Shopify && window.Shopify.captcha) {
      window.Shopify.captcha.protect(form, () => {
        this.debug('Submitting login form with CAPTCHA protection');
        // Form will be submitted after CAPTCHA validation
      });
      form.submit();
    } else {
      // Submit without CAPTCHA if not available
      this.debug('Submitting login form without CAPTCHA protection');
      form.submit();
    }
  }

  /**
   * Handle customer registration forms (fallback implementation)
   * @param {Object} formFields - The form fields
   */
  handleRegister(formFields) {
    this.debug('Processing registration form (fallback implementation)');

    // Extract form fields for customer registration
    const email = formFields['customer[email]'] || formFields.email;
    const password = formFields['customer[password]'] || formFields.password;
    const firstName = formFields['customer[first_name]'] || formFields.first_name;
    const lastName = formFields['customer[last_name]'] || formFields.last_name;
    const phone = formFields['customer[note][phone]'] || formFields.phone;
    const acceptsMarketing = formFields['customer[accepts_marketing]'] === '1' ||
                            formFields.accepts_marketing === '1' ||
                            formFields.newsletter === 'on';

    if (!email || !password) {
      console.error('FormEventHandler: Missing email or password for registration');
      return;
    }

    // Create the form submission
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account';
    form.style.display = 'none'; // Hide the form

    // Required form type field
    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'create_customer';
    form.appendChild(formTypeInput);

    // UTF8 field
    const utf8Input = document.createElement('input');
    utf8Input.type = 'hidden';
    utf8Input.name = 'utf8';
    utf8Input.value = '✓';
    form.appendChild(utf8Input);

    // Email field
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = email;
    form.appendChild(emailInput);

    // Password field
    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = password;
    form.appendChild(passwordInput);

    // First name (if provided)
    if (firstName) {
      const firstNameInput = document.createElement('input');
      firstNameInput.type = 'hidden';
      firstNameInput.name = 'customer[first_name]';
      firstNameInput.value = firstName;
      form.appendChild(firstNameInput);
    }

    // Last name (if provided)
    if (lastName) {
      const lastNameInput = document.createElement('input');
      lastNameInput.type = 'hidden';
      lastNameInput.name = 'customer[last_name]';
      lastNameInput.value = lastName;
      form.appendChild(lastNameInput);
    }

    // Phone (if provided)
    if (phone) {
      const phoneInput = document.createElement('input');
      phoneInput.type = 'hidden';
      phoneInput.name = 'customer[note][phone]';
      phoneInput.value = phone;
      form.appendChild(phoneInput);
    }

    // Marketing acceptance
    if (acceptsMarketing) {
      const marketingInput = document.createElement('input');
      marketingInput.type = 'hidden';
      marketingInput.name = 'customer[accepts_marketing]';
      marketingInput.value = '1';
      form.appendChild(marketingInput);
    }

    // Append the form to the document body
    document.body.appendChild(form);

    // Submit the form
    this.debug('Submitting customer registration form');
    form.submit();
  }

  /**
   * Handle password recovery forms (fallback implementation)
   * @param {Object} formFields - The form fields
   */
  handlePasswordRecovery(formFields) {
    this.debug('Processing password recovery form (fallback implementation)');

    // Will implement password recovery logic when needed
  }

  /**
   * Handle contact forms (fallback implementation)
   * @param {Object} formFields - The form fields
   */
  handleContact(formFields) {
    this.debug('Processing contact form (fallback implementation)');

    // Will implement contact form handling when needed
  }

  /**
   * Get hook status for diagnostics
   * @returns {Object} Status object with registry information
   */
  getStatus() {
    return {
      initialized: true,
      registeredHooks: Object.keys(this.hookRegistry),
      availableHandlers: Object.keys(this.handlers)
    };
  }
}

// Create singleton instance
const formEventHandler = new FormEventHandler();

// Expose on window for global access
if (typeof window !== 'undefined') {
  window.formEventHandler = formEventHandler;
}

export default formEventHandler;
